import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, Image, Plus, Trash2, Info, X, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { id: 'fuego_calor', name: 'Fuego/Calor', description: 'Volcanes, lava, desiertos, sol', opposite: 'agua_frio' },
  { id: 'agua_frio', name: 'Agua/Frío', description: 'Glaciares, océano, cascadas', opposite: 'fuego_calor' },
  { id: 'naturaleza_estatica', name: 'Naturaleza estática', description: 'Montañas, rocas, cuevas', opposite: 'naturaleza_dinamica' },
  { id: 'naturaleza_dinamica', name: 'Naturaleza dinámica', description: 'Tormentas, olas, viento', opposite: 'naturaleza_estatica' },
  { id: 'estructuras', name: 'Estructuras', description: 'Templos, pirámides, puentes', opposite: 'texturas_extremas' },
  { id: 'texturas_extremas', name: 'Texturas extremas', description: 'Cristales, arena, metal', opposite: 'estructuras' },
  { id: 'espacial_cosmico', name: 'Espacial/Cósmico', description: 'Galaxias, nebulosas, planetas', opposite: 'organico_macro' },
  { id: 'organico_macro', name: 'Orgánico macro', description: 'Flores, bosques, selva', opposite: 'espacial_cosmico' },
  { id: 'urbano_tecnologico', name: 'Urbano/Tecnológico', description: 'Ciudades, neon, arquitectura moderna', opposite: 'rural_natural' },
  { id: 'rural_natural', name: 'Rural/Natural', description: 'Campos, granjas, praderas', opposite: 'urbano_tecnologico' },
  { id: 'luz_brillante', name: 'Luz/Brillante', description: 'Amaneceres, rayos de sol, destellos', opposite: 'oscuridad_sombras' },
  { id: 'oscuridad_sombras', name: 'Oscuridad/Sombras', description: 'Cuevas, siluetas, niebla', opposite: 'luz_brillante' }
];

export default function AdminImages() {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [uploadUrl, setUploadUrl] = useState('');
  const [uploadCategory, setUploadCategory] = useState('fuego_calor');
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchImages();
  }, [isAdmin]);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setImages(await response.json());
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddImage = async () => {
    if (!uploadUrl.trim()) {
      toast.error('Ingresa una URL de imagen');
      return;
    }

    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/api/images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url: uploadUrl,
          category: uploadCategory,
          tags: []
        })
      });

      if (!response.ok) throw new Error('Error al agregar imagen');

      toast.success('Imagen agregada');
      setUploadUrl('');
      setShowUpload(false);
      fetchImages();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo es muy grande. Máximo 5MB.');
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato no válido. Usa JPG, PNG o WebP.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', uploadCategory);

    try {
      const response = await fetch(`${API_URL}/api/images/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Error al subir imagen');

      toast.success('Imagen subida');
      setShowUpload(false);
      fetchImages();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      const response = await fetch(`${API_URL}/api/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al eliminar');
      }

      toast.success('Imagen eliminada');
      setDeleteConfirm(null);
      fetchImages();
    } catch (error) {
      toast.error(error.message || 'Error al eliminar imagen');
    }
  };

  const filteredImages = selectedCategory === 'all' 
    ? images 
    : images.filter(img => img.category === selectedCategory);

  const getCategoryCount = (catId) => images.filter(img => img.category === catId).length;

  if (loading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="h-8 bg-white/5 rounded w-48 mb-8"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="aspect-square bg-white/5 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Banco de Imágenes</h1>
          <p className="text-white/50 mt-1">{images.length} imágenes en {CATEGORIES.length} categorías</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowGuide(true)}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Info className="w-4 h-4" />
            Guía de imágenes
          </button>
          <button
            onClick={() => setShowUpload(true)}
            data-testid="add-image-button"
            className="flex items-center gap-2 bg-[hsl(198,62%,54%)] hover:bg-[hsl(198,62%,48%)] text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Agregar imagen
          </button>
        </div>
      </div>

      {/* Category Stats - Grid 3x4 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
        {CATEGORIES.map(cat => {
          const oppositeCat = CATEGORIES.find(c => c.id === cat.opposite);
          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(selectedCategory === cat.id ? 'all' : cat.id)}
              className={`p-3 rounded-xl border text-left transition-colors ${
                selectedCategory === cat.id
                  ? 'border-[hsl(198,62%,54%)] bg-[hsl(198,62%,54%)]/10'
                  : 'border-[hsl(232,18%,18%)] bg-[hsl(232,36%,9%)] hover:border-white/20'
              }`}
            >
              <p className="text-sm text-white/50 mb-1 truncate">{cat.name}</p>
              <p className="text-xl font-bold text-white">{getCategoryCount(cat.id)}</p>
              <p className="text-xs text-white/30 mt-1">↔ {oppositeCat?.name}</p>
            </button>
          );
        })}
      </div>

      {/* Images Grid */}
      <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-white">
            {selectedCategory === 'all' ? 'Todas las imágenes' : CATEGORIES.find(c => c.id === selectedCategory)?.name}
            <span className="text-white/50 ml-2">({filteredImages.length})</span>
          </h2>
          {selectedCategory !== 'all' && (
            <button
              onClick={() => setSelectedCategory('all')}
              className="text-sm text-[hsl(198,62%,54%)] hover:underline"
            >
              Ver todas
            </button>
          )}
        </div>

        {filteredImages.length === 0 ? (
          <div className="text-center py-12">
            <Image className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/50">No hay imágenes en esta categoría</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredImages.map((image) => (
              <div key={image.id} className="group relative aspect-square rounded-xl overflow-hidden border border-[hsl(232,18%,18%)]">
                <img
                  src={image.url.startsWith('http') ? image.url : `${API_URL}${image.url}`}
                  alt={image.category}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/200?text=Error';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-xs text-white/80 truncate">
                      {CATEGORIES.find(c => c.id === image.category)?.name}
                    </p>
                  </div>
                  {/* Delete button */}
                  <button
                    onClick={() => setDeleteConfirm(image)}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500/80 hover:bg-red-500 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Agregar imagen</h2>
              <button onClick={() => setShowUpload(false)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-2">Categoría</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  data-testid="upload-category-select"
                  className="w-full bg-[hsl(232,20%,14%)] border border-[hsl(232,18%,18%)] rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(198,62%,54%)]"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name} - {cat.description}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-2">URL de imagen</label>
                <input
                  type="url"
                  value={uploadUrl}
                  onChange={(e) => setUploadUrl(e.target.value)}
                  data-testid="upload-url-input"
                  className="w-full bg-[hsl(232,20%,14%)] border border-[hsl(232,18%,18%)] rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[hsl(198,62%,54%)]"
                  placeholder="https://..."
                />
              </div>

              <div className="relative">
                <div className="text-center text-white/50 text-sm py-2">o</div>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-2">Subir archivo</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileUpload}
                  data-testid="upload-file-input"
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="flex items-center justify-center gap-2 w-full py-4 border-2 border-dashed border-[hsl(232,18%,25%)] rounded-lg cursor-pointer hover:border-[hsl(198,62%,54%)] transition-colors"
                >
                  <Upload className="w-5 h-5 text-white/50" />
                  <span className="text-white/50">JPG, PNG o WebP (máx. 5MB)</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowUpload(false)}
                className="flex-1 bg-white/10 hover:bg-white/15 text-white py-3 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddImage}
                disabled={uploading || !uploadUrl.trim()}
                data-testid="submit-image-button"
                className="flex-1 bg-[hsl(198,62%,54%)] hover:bg-[hsl(198,62%,48%)] disabled:opacity-50 text-white py-3 rounded-lg transition-colors"
              >
                {uploading ? 'Guardando...' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h2 className="text-lg font-semibold text-white">¿Eliminar imagen?</h2>
            </div>
            
            <div className="mb-4">
              <img
                src={deleteConfirm.url.startsWith('http') ? deleteConfirm.url : `${API_URL}${deleteConfirm.url}`}
                alt="Preview"
                className="w-full h-32 object-cover rounded-lg"
              />
            </div>
            
            <p className="text-white/60 text-sm mb-6">
              Esta acción no se puede deshacer. La imagen será eliminada permanentemente.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 bg-white/10 hover:bg-white/15 text-white py-2.5 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteImage(deleteConfirm.id)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-lg transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 w-full max-w-2xl my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Guía para subir imágenes</h2>
              <button onClick={() => setShowGuide(false)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6 text-sm">
              {/* Format & Size */}
              <div className="bg-[hsl(232,20%,14%)] rounded-lg p-4">
                <h3 className="font-medium text-white mb-3">📐 Formato y Tamaño</h3>
                <ul className="space-y-2 text-white/70">
                  <li>• <strong className="text-white">Formatos:</strong> JPG, PNG o WebP</li>
                  <li>• <strong className="text-white">Tamaño máximo:</strong> 5 MB por imagen</li>
                  <li>• <strong className="text-white">Resolución recomendada:</strong> 800x600 píxeles mínimo</li>
                  <li>• <strong className="text-white">Proporción:</strong> Preferible 4:3 o cuadrada</li>
                </ul>
              </div>

              {/* Content Guidelines */}
              <div className="bg-[hsl(232,20%,14%)] rounded-lg p-4">
                <h3 className="font-medium text-white mb-3">🎨 Características de las imágenes</h3>
                <ul className="space-y-2 text-white/70">
                  <li>• <strong className="text-white">Claridad:</strong> Imágenes nítidas y bien definidas</li>
                  <li>• <strong className="text-white">Sin texto:</strong> Evitar imágenes con palabras o números</li>
                  <li>• <strong className="text-white">Sin rostros:</strong> Evitar fotos de personas reconocibles</li>
                  <li>• <strong className="text-white">Alto contraste sensorial:</strong> Colores y texturas distintivas</li>
                  <li>• <strong className="text-white">Fácil de describir:</strong> Elementos visuales claros</li>
                </ul>
              </div>

              {/* Opposites Rule */}
              <div className="bg-[hsl(198,62%,54%)]/10 border border-[hsl(198,62%,54%)]/30 rounded-lg p-4">
                <h3 className="font-medium text-white mb-3">⚡ Regla de Opuestos Sensoriales</h3>
                <p className="text-white/70 mb-3">
                  Cada par de imágenes A/B debe ser <strong className="text-white">opuesto sensorial</strong>:
                </p>
                <div className="grid grid-cols-2 gap-3 text-white/70">
                  <div className="bg-black/20 rounded p-2">
                    <p className="text-xs text-white/50">Si A es...</p>
                    <p>🔥 Fuego/Caliente</p>
                  </div>
                  <div className="bg-black/20 rounded p-2">
                    <p className="text-xs text-white/50">B debe ser...</p>
                    <p>❄️ Agua/Frío</p>
                  </div>
                  <div className="bg-black/20 rounded p-2">
                    <p className="text-xs text-white/50">Si A es...</p>
                    <p>☀️ Luz/Brillante</p>
                  </div>
                  <div className="bg-black/20 rounded p-2">
                    <p className="text-xs text-white/50">B debe ser...</p>
                    <p>🌑 Oscuro/Sombras</p>
                  </div>
                </div>
                <p className="text-white/50 text-xs mt-3">
                  El sistema empareja automáticamente categorías opuestas para cada sesión ARV.
                </p>
              </div>

              {/* Categories */}
              <div className="bg-[hsl(232,20%,14%)] rounded-lg p-4">
                <h3 className="font-medium text-white mb-3">📁 Pares de Categorías (Opuestos)</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-white/70">
                    <span>🔥 Fuego/Calor</span>
                    <span className="text-white/30">↔</span>
                    <span>❄️ Agua/Frío</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <span>🏔️ Estático</span>
                    <span className="text-white/30">↔</span>
                    <span>🌊 Dinámico</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <span>🏛️ Estructuras</span>
                    <span className="text-white/30">↔</span>
                    <span>💎 Texturas</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <span>🌌 Cósmico</span>
                    <span className="text-white/30">↔</span>
                    <span>🌿 Orgánico</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <span>🏙️ Urbano</span>
                    <span className="text-white/30">↔</span>
                    <span>🌾 Rural</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/70">
                    <span>☀️ Luz</span>
                    <span className="text-white/30">↔</span>
                    <span>🌑 Oscuridad</span>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-[hsl(252,37%,60%)]/10 border border-[hsl(252,37%,60%)]/30 rounded-lg p-4">
                <h3 className="font-medium text-white mb-2">💡 Tip</h3>
                <p className="text-white/70">
                  Mantén al menos <strong className="text-white">8-10 imágenes por categoría</strong> para 
                  asegurar variedad en las sesiones. Cuantas más imágenes, menos repetición.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowGuide(false)}
              className="w-full mt-6 bg-white/10 hover:bg-white/15 text-white py-3 rounded-lg transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
